import { SideImage } from '../../components/SideImage';
import { StickyFooter } from '../../components/StickyFooter';
import { BookingNotificationContent } from '../../composites/BookingNotificationContent';
import { BookingTopBar } from '../../composites/BookingTopBar';
import { SpaTreatmentFacilityBookingContent } from '../../composites/SpaTreatmentFacilityBookingContent';
import { SplitLayout } from '../../composites/SplitLayout';
import type { SpaTreatmentFacilityBookingFormProps } from './SpaTreatmentFacilityBookingForm.types';

export function SpaTreatmentFacilityBookingForm({
  embedded = false,
  imageUrl,
  imageAlt,
  onBack,
  backLabel,
  progressStep,
  progressTotal,
  progressLabel,
  progressValueText,
  telephoneOnly,
  unavailable,
  stickyFooter,
  labels,
  ...contentProps
}: SpaTreatmentFacilityBookingFormProps) {
  const {
    title: pageTitle,
    subtitle: pageSubtitle,
    noteText,
    calendar,
    stickyFooter: stickyFooterLabels,
    telephoneOnly: telephoneOnlyLabels,
    unavailable: unavailableLabels,
    ...contentLabels
  } = labels;

  const title = embedded ? undefined : pageTitle;
  const subtitle = embedded ? undefined : pageSubtitle;
  const showTelephoneOnly = telephoneOnly && !!telephoneOnlyLabels;
  const showUnavailable = unavailable && !!unavailableLabels;

  if (process.env.NODE_ENV !== 'production') {
    if (telephoneOnly && !telephoneOnlyLabels) {
      console.warn(
        'SpaTreatmentFacilityBookingForm: telephoneOnly is true but labels.telephoneOnly is missing. The booking form will render as normal.'
      );
    }
    if (unavailable && !unavailableLabels) {
      console.warn(
        'SpaTreatmentFacilityBookingForm: unavailable is true but labels.unavailable is missing. The booking form will render as normal.'
      );
    }
  }

  const content = (
    <>
      {showTelephoneOnly ? (
        <BookingNotificationContent
          title={title}
          subtitle={subtitle}
          variant="telephoneOnly"
          notificationLabels={telephoneOnlyLabels!}
        />
      ) : showUnavailable ? (
        <BookingNotificationContent
          title={title}
          subtitle={subtitle}
          variant="unavailable"
          notificationLabels={{
            ...unavailableLabels!,
            onCtaClick: unavailableLabels!.onCtaClick ?? onBack,
          }}
          reservationProps={{
            title: contentLabels.reservation?.title,
            subtitle: contentLabels.reservation?.subtitle,
            guestLabel: contentLabels.reservation?.guestLabel,
            guestCount: contentProps.guestCount,
            minGuests: contentProps.minGuests,
            maxGuests: contentProps.maxGuests,
            onGuestCountChange: contentProps.onGuestCountChange,
            activeDate: contentProps.activeDate,
            selectedDate: contentProps.selectedDate,
            calendarView: contentProps.calendarView,
            availability: contentProps.availability,
            onDateSelect: contentProps.onDateSelect,
            onWeekNavigate: contentProps.onWeekNavigate,
            onCalendarViewChange: contentProps.onCalendarViewChange,
            calendarLabels: calendar,
            counterDecreaseLabel: contentLabels.counter?.decrease,
            counterIncreaseLabel: contentLabels.counter?.increase,
          }}
        />
      ) : (
        <>
          <SpaTreatmentFacilityBookingContent
            {...contentProps}
            title={title}
            subtitle={subtitle}
            noteText={noteText}
            calendarLabels={calendar}
            labels={contentLabels}
          />
          {stickyFooter && stickyFooterLabels && (
            <StickyFooter
              {...stickyFooter}
              totalLabel={stickyFooterLabels.totalLabel}
              ctaLabel={stickyFooterLabels.ctaLabel}
              inline={embedded}
            />
          )}
        </>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <SplitLayout
      hideLeftOnMobile
      left={<SideImage src={imageUrl} alt={imageAlt} sticky onBack={onBack} />}
      right={
        <>
          <BookingTopBar
            desktopHidden
            inset={false}
            onBack={onBack}
            backLabel={backLabel}
            progressStep={progressStep}
            progressTotal={progressTotal}
            progressLabel={progressLabel}
            progressValueText={progressValueText}
          />
          {content}
        </>
      }
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
